import { Map } from 'immutable';

import * as _ from 'lodash';

import { Action } from '../actions';
import {
    calculateInitialTemplateData,
    doesHover,
    SubSection,
    TemplateData
} from "../services/template-service";
import { getTemplates } from "../services/template-store";
import { Locale } from "../services/translation-store";


export interface TemplateOverview {
    readonly id: number,
    readonly title: string,
    readonly description: string,
    readonly thumb: string,
}

export interface TemplateState {
    readonly currentTemplate: TemplateData,
}

const EMPTY_TEMPLATE_DATA: TemplateData = {
    canvasWidth: 1920,
    canvasHeight: 1080,
    storyboardWidth: 1920,
    storyboardHeight: 1080,
    sections: [],
    sectionsWidth: 0,
    texts: Map(),
};


const initialState: TemplateState = {
    currentTemplate: EMPTY_TEMPLATE_DATA,
};


export default function handleAction(state: TemplateState = initialState, action: Action): TemplateState {
    switch (action.type) {

        case 'SET_TEMPLATE_ID':
            return {
                ...state,
                currentTemplate: calculateInitialTemplateData(action.newTemplateId),
            };

        case 'SET_PRESENTATION':
            return {
                ...state,
                currentTemplate: calculateInitialTemplateData(action.presentation.templateId),
            };

        case 'START_NOTE_DRAGGING':
            return {
                ...state,
                currentTemplate: calculateNoteHover(state.currentTemplate, action.noteX, action.noteY),
            };

        case 'NOTE_DRAGGING':
            return {
                ...state,
                currentTemplate: calculateNoteHover(state.currentTemplate, action.endX, action.endY),
            };

        case 'START_SLIDE_DRAGGING':
            return {
                ...state,
                currentTemplate: calculateSlideHover(state.currentTemplate, action.slideX, action.slideY),
            };

        case 'SLIDE_DRAGGING':
            return {
                ...state,
                currentTemplate: calculateSlideHover(state.currentTemplate, action.endX, action.endY),
            };

        case 'STOP_NOTE_DRAGGING':
        case 'STOP_SLIDE_DRAGGING':
        case 'SET_PROCESS_STEP':
            return {
                ...state,
                currentTemplate: resetHover(state.currentTemplate)
            };

        case 'UNDO':
            return handleUndo(state, action.undoAction);

        case 'REDO':
            return handleAction(state, action.redoAction);

        default:
            return state;
    }
}

function handleUndo(state: TemplateState, action: Action): TemplateState {
    switch (action.type) {

        case 'SET_TEMPLATE_ID':
            return {
                ...state,
                currentTemplate: calculateInitialTemplateData(action.oldTemplateId),
            };

        default:
            return state;
    }
}


function calculateNoteHover(templateData: TemplateData, x: number, y: number): TemplateData {
    return {
        ...templateData,
        sections: templateData.sections.map(
            section => {
                return {
                    ...section,
                    noteHovered: doesHover(x, y, section.canvasData.hotspot),
                    subSections: section.subSections.map(
                        (subSection: SubSection) => {
                            return {
                                ...subSection,
                                hovered: doesHover(x, y, subSection.hotspot)
                            }
                        }
                    ),
                }
            }
        )
    };
}

function calculateSlideHover(templateData: TemplateData, x: number, y: number): TemplateData {
    return {
        ...templateData,
        sections: templateData.sections.map(
            section => {
                return {
                    ...section,
                    slideHovered: doesHover(x, y, section.storyboardData.hotspotSlides),
                }
            }
        )
    };
}

function resetHover(templateData: TemplateData): TemplateData {
    return {
        ...templateData,
        sections: templateData.sections.map(
            section => ({
                ...section,
                noteHovered: false,
                slideHovered: false,
                subSections: section.subSections.map(
                    (subSection: SubSection) => ({
                        ...subSection,
                        hovered: false,
                    })
                ),
            })
        )
    };
}

export const getTemplateOverviews = (state: TemplateState, locale: Locale): ReadonlyArray<TemplateOverview> =>
    getTemplates().map(
        template => ({
            id: template.id,
            title: _.get(template, ['texts', locale, template.title], ''),
            description: _.get(template, ['texts', locale, template.description], ''),
            thumb: template.thumb,
        })
    );

export const getCurrentTemplate = (state: TemplateState): TemplateData => state.currentTemplate;

export function getTemplateText(state: TemplateState, locale: Locale, id: string): string {
    if (!state.currentTemplate.texts) {
        return '';
    }
    const translations = state.currentTemplate.texts.get(locale);
    if (!translations) {
        return '';
    }
    return translations.get(id) || '';
}



// WEBPACK FOOTER //
// ./src/reducers/template.ts